const CART_DRAWER_OPEN_ON_LOAD_KEY = 'cp:open-cart-drawer';
const cartDrawerBoundLinks = new WeakSet();

function normalizePathname(pathname) {
  const normalizedPathname = pathname.replace(/\/+$/, '');
  return normalizedPathname || '/';
}

function getCartUrl() {
  return new URL(window.routes?.cart_url || '/cart', window.location.origin);
}

function getRootUrl() {
  return new URL(window.routes?.root_url || '/', window.location.origin);
}

function isCartPage(url = window.location) {
  return normalizePathname(url.pathname) === normalizePathname(getCartUrl().pathname);
}

function isCartNavigationLink(link) {
  if (!link?.href || link.hasAttribute('download') || link.target === '_blank') return false;

  const linkUrl = new URL(link.href, window.location.origin);
  return linkUrl.origin === window.location.origin && isCartPage(linkUrl);
}

function getCartRedirectUrl() {
  const fallbackUrl = getRootUrl();

  if (!document.referrer) return fallbackUrl;

  try {
    const referrerUrl = new URL(document.referrer);
    if (referrerUrl.origin !== window.location.origin || isCartPage(referrerUrl)) {
      return fallbackUrl;
    }

    return referrerUrl;
  } catch {
    return fallbackUrl;
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setCartLinkAccessibility();

    if (this.redirectCartPage()) return;
    this.openFromRedirect();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    if (cartDrawerBoundLinks.has(cartLink)) return;
    cartDrawerBoundLinks.add(cartLink);

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      document.querySelector('cart-drawer')?.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        document.querySelector('cart-drawer')?.open(cartLink);
      }
    });
  }

  setCartLinkAccessibility() {
    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('a');
      if (!cartLink || cartLink.id === 'cart-icon-bubble') return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      if (!isCartNavigationLink(cartLink)) return;

      event.preventDefault();
      this.open(cartLink);
    });
  }

  redirectCartPage() {
    if (window.Shopify?.designMode || !isCartPage() || this.classList.contains('is-empty')) return false;

    sessionStorage.setItem(CART_DRAWER_OPEN_ON_LOAD_KEY, 'true');
    window.location.replace(getCartRedirectUrl());
    return true;
  }

  openFromRedirect() {
    if (sessionStorage.getItem(CART_DRAWER_OPEN_ON_LOAD_KEY) !== 'true') return;

    sessionStorage.removeItem(CART_DRAWER_OPEN_ON_LOAD_KEY);
    requestAnimationFrame(() => {
      this.open(document.querySelector('#cart-icon-bubble'));
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id || parsedState.items?.[0]?.id;
    const drawerSectionHtml = parsedState.sections?.['cart-drawer'];

    if (drawerSectionHtml) {
      const sourceDrawer = this.getSectionDOM(drawerSectionHtml, 'cart-drawer');

      if (sourceDrawer) {
        this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
      }
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHtml) return;
      const sectionInnerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (sectionInnerHTML == null) return;

      sectionElement.innerHTML = sectionInnerHTML;
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    if (!selector) return parsedHTML.body.innerHTML;

    const selectedElement = parsedHTML.querySelector(selector);
    return selectedElement ? selectedElement.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer .drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
